/**
 * HealthIndicator Component
 * Phase 7.16: Navigation Health Status Indicator
 *
 * A 16×16 coloured dot in the navigation header that shows live API and
 * database health status. Hovering for 500 ms reveals a tooltip panel.
 */

'use client';

import { useHealthStatus, type HealthState, type HealthStatus } from '@/hooks/use-health-status';
import { useRef, useState } from 'react';

const TOOLTIP_DELAY_MS = 500;
const TOOLTIP_ID = 'health-tooltip';

function dotClasses(status: HealthStatus): string {
  const base = 'w-4 h-4 rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1';
  switch (status) {
    case 'checking':
      return `${base} bg-gray-400`;
    case 'healthy':
      return `${base} bg-green-500 ring-2 ring-green-200`;
    case 'degraded':
      return `${base} bg-amber-400 animate-pulse ring-2 ring-amber-200`;
    case 'unhealthy':
      return `${base} bg-red-500 ring-2 ring-red-200`;
  }
}

function dotAriaLabel(status: HealthStatus): string {
  switch (status) {
    case 'checking':
      return 'System health: checking…';
    case 'healthy':
      return 'System health: all systems healthy';
    case 'degraded':
      return 'System health: database resuming';
    case 'unhealthy':
      return 'System health: system unavailable';
  }
}

interface DbStatusDisplayProps {
  status: HealthState['database']['status'];
}

function DbStatusDisplay({ status }: DbStatusDisplayProps): JSX.Element {
  switch (status) {
    case 'healthy':
      return <span className="text-green-600">Online</span>;
    case 'resuming':
      return <span className="text-amber-600">Resuming…</span>;
    case 'unhealthy':
      return <span className="text-red-600">Error</span>;
    default:
      return <span className="text-gray-500">Unknown</span>;
  }
}

interface HealthTooltipProps {
  health: HealthState;
}

function HealthTooltip({ health }: HealthTooltipProps): JSX.Element {
  return (
    <div
      id={TOOLTIP_ID}
      role="tooltip"
      className="absolute right-0 top-6 z-50 min-w-48 rounded-lg border border-gray-200 bg-white p-3 shadow-lg text-xs text-gray-700"
    >
      {health.status === 'checking' ? (
        <>
          <div className="mb-2 h-3 w-24 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
        </>
      ) : (
        <>
          <p className="mb-2 font-semibold text-gray-900 text-xs">System Health</p>
          <div className="space-y-1">
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">API response</span>
              <span className="font-medium">
                {health.apiResponseTimeMs !== null ? `${health.apiResponseTimeMs} ms` : '—'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">Database</span>
              <DbStatusDisplay status={health.database.status} />
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">DB response</span>
              <span className="font-medium">
                {health.database.latencyMs !== null ? `${health.database.latencyMs} ms` : '—'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">Last checked</span>
              <span className="font-medium">
                {health.lastChecked
                  ? health.lastChecked.toLocaleTimeString()
                  : '—'}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function HealthIndicator(): JSX.Element {
  const health = useHealthStatus();
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = (): void => {
    timerRef.current = setTimeout(() => setVisible(true), TOOLTIP_DELAY_MS);
  };

  const hide = (): void => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  };

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <div
        role="status"
        aria-label={dotAriaLabel(health.status)}
        aria-describedby={visible ? TOOLTIP_ID : undefined}
        tabIndex={0}
        onFocus={() => setVisible(true)}
        onBlur={hide}
        className={dotClasses(health.status)}
      />
      {visible && <HealthTooltip health={health} />}
    </div>
  );
}
